import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SnackbarComponent} from '@app/support/snackbars/snackbar.component';
import {BehaviorSubject, Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {EQOLogLevel, QOLogService} from '@quickorder/angular-web-lib';

@Injectable()
export class SnackBar {
	public snackActions: BehaviorSubject<any[]> = new BehaviorSubject([]);
	public subscriptions: Subscription[] = [];
	public EQOLogLevel = EQOLogLevel;

	constructor(
		public snackBar: MatSnackBar,
		public translate: TranslateService,
		private logService: QOLogService,
	) {
	}

	public snackAction(title, text, buttons = [], timeout = 0, type = 'success'): void {
		const snackAction = {
			title, text, buttons, type, remove: () => {
				const newSnackActions = [];
				this.snackActions.getValue().forEach((snackActionToCheck) => {
					if (snackAction !== snackActionToCheck) {
						newSnackActions.push(snackActionToCheck);
					}
				});
				this.snackActions.next(newSnackActions);
			},
		};
		const snackActions = this.snackActions.getValue();
		snackActions.push(snackAction);
		this.snackActions.next(snackActions);
		if (timeout !== 0) {
			setTimeout(() => {
				snackAction.remove();
			}, timeout);
		}
	}

	public success(transKey, content?, title?): void {
		if (title === undefined) {
			title = this.translate.instant('SNACKBAR.DONE');
		}
		if (typeof (content) === 'string') {
			content = {x: content};
		}
		content = this.translate.instant('SNACKBAR.' + transKey, content);

		this.snackBar.openFromComponent(SnackbarComponent, {
			duration: 3000,
			horizontalPosition: 'end',
			data: {content, title, type: 'success'},
		});
	}

	public error(transKey, content = {}, title?): void {
		if (title === undefined) {
			title = this.translate.instant('SNACKBAR.ERROR');
		}
		if (typeof (content) === 'string') {
			content = {x: content};
		}
		content = this.translate.instant('SNACKBAR.' + transKey, content);
		this.snackBar.openFromComponent(SnackbarComponent, {
			duration: 6000,
			horizontalPosition: 'end',
			data: {content, title, type: 'danger'},
		});
	}

	public confirm(content, title, action): void {
		this.snackBar.open(content, action);
	}

	public newSuccess(transKey?, content?, titleKey = 'SNACKBAR.DONE'): void {
		const title = this.translate.instant(titleKey);
		if (transKey) {
			if (typeof (content) === 'string') {
				content = {x: content};
			}
			content = this.translate.instant(transKey, content);
		}

		const data = {
			level: this.EQOLogLevel.SUCCESS,
			title,
			actions: [],
		};
		if (transKey) {
			data['description'] = content;
		}
		this.logService.logContent(data);
	}

	public newError(transKey: string, content?, titleKey?: string, forceDuration?: number): void {
		if (titleKey === undefined) {
			titleKey = 'SNACKBAR.ERROR';
		}
		const title = this.translate.instant(titleKey);
		if (typeof (content) === 'string') {
			content = {x: content};
		}
		content = this.translate.instant(transKey, content);
		const logContent = {
			level: this.EQOLogLevel.ERROR,
			title,
			description: content,
			actions: [],
		};
		if (forceDuration) {
			logContent['forceDuration'] = forceDuration;
		}
		this.logService.logContent(logContent);
	}

	public newWarning(timeOut: number = 1500, transKey?, content?, titleKey = 'SNACKBAR.WARNING'): void {
		const title = this.translate.instant(titleKey);
		if (transKey) {
			if (typeof (content) === 'string') {
				content = {x: content};
			}
			content = this.translate.instant(transKey, content);
		}

		const data = {
			level: this.EQOLogLevel.WARNING,
			title,
			actions: [],
			forceDuration: timeOut,
		};
		if (transKey) {
			data['description'] = content;
		}
		this.logService.logContent(data);
	}
}
